import maxBy from "lodash/maxBy";
import uniq from "lodash/uniq";
import { useMemo } from "react";

/**
 * @param aspectRatios array of aspect ratios as floats
 * @param defaultRatio (Optional) default aspect ratio if one cannot be determined
 *
 * @returns Aspect ratio as float
 */
export function useAspectRatio(
  aspectRatios: number[],
  defaultRatio = 1,
): number {
  return useMemo(() => {
    if (aspectRatios.length === 0) {
      return defaultRatio;
    }

    // corrects for issues where ratios like 1.778 don't match 1.7778
    const roundedAspectRatios = aspectRatios.map((ratio) => ratio.toFixed(3));

    const uniqueAspectRatios = uniq(roundedAspectRatios);

    if (
      uniqueAspectRatios.length === 1 &&
      uniqueAspectRatios[0] !== undefined
    ) {
      return Number.parseFloat(uniqueAspectRatios[0]);
    }

    if (uniqueAspectRatios.length === roundedAspectRatios.length) {
      return defaultRatio;
    }

    const ratioCounts = roundedAspectRatios.reduce<Record<string, number>>(
      (prev, ratio) => ({ ...prev, [ratio]: (prev[ratio] || 0) + 1 }),
      {},
    );

    const max = maxBy(Object.entries(ratioCounts), ([, count]) => count);

    return max ? Number.parseFloat(max[0]) : defaultRatio;
  }, [aspectRatios, defaultRatio]);
}
